import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// ─── Animated Broken Screen SVG Illustration ───────────────────────────
function BrokenScreenIllustration() {
  return (
    <svg
      viewBox="0 0 200 200"
      className="h-44 w-44 sm:h-52 sm:w-52 drop-shadow-2xl"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Outer glow pulse */}
      <circle
        cx="100"
        cy="100"
        r="90"
        className="animate-pulse-slow fill-red-500/5 dark:fill-red-400/5"
      />

      {/* Monitor body */}
      <rect
        x="35"
        y="30"
        width="130"
        height="100"
        rx="12"
        className="fill-gray-100 stroke-gray-300 dark:fill-gray-800 dark:stroke-gray-600"
        strokeWidth="2.5"
      />

      {/* Screen area */}
      <rect
        x="43"
        y="38"
        width="114"
        height="76"
        rx="6"
        className="fill-gradient-screen"
      />
      <defs>
        <linearGradient
          id="screenGrad"
          x1="43"
          y1="38"
          x2="157"
          y2="114"
        >
          <stop
            offset="0%"
            className="[stop-color:theme(colors.red.50)] dark:[stop-color:theme(colors.red.950)]"
          />
          <stop
            offset="100%"
            className="[stop-color:theme(colors.orange.50)] dark:[stop-color:theme(colors.red.900)]"
          />
        </linearGradient>
      </defs>
      <rect
        x="43"
        y="38"
        width="114"
        height="76"
        rx="6"
        fill="url(#screenGrad)"
      />

      {/* Crack lines on screen */}
      <path
        d="M100 38 L92 58 L105 68 L88 85 L100 114"
        className="stroke-red-400 dark:stroke-red-500"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        <animate
          attributeName="stroke-dasharray"
          from="0,200"
          to="200,0"
          dur="1.5s"
          fill="freeze"
        />
      </path>
      <path
        d="M92 58 L75 52"
        className="stroke-red-300 dark:stroke-red-600"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      >
        <animate
          attributeName="stroke-dasharray"
          from="0,50"
          to="50,0"
          dur="1s"
          begin="0.5s"
          fill="freeze"
        />
      </path>
      <path
        d="M105 68 L122 62"
        className="stroke-red-300 dark:stroke-red-600"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      >
        <animate
          attributeName="stroke-dasharray"
          from="0,50"
          to="50,0"
          dur="1s"
          begin="0.7s"
          fill="freeze"
        />
      </path>
      <path
        d="M88 85 L72 90"
        className="stroke-red-300 dark:stroke-red-600"
        strokeWidth="1.5"
        strokeLinecap="round"
        fill="none"
      >
        <animate
          attributeName="stroke-dasharray"
          from="0,50"
          to="50,0"
          dur="1s"
          begin="0.9s"
          fill="freeze"
        />
      </path>

      {/* Sad face on screen */}
      <g className="animate-fade-in" style={{ animationDelay: '0.8s' }}>
        {/* Left eye */}
        <line
          x1="78"
          y1="58"
          x2="84"
          y2="64"
          className="stroke-red-400/60 dark:stroke-red-400/50"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="84"
          y1="58"
          x2="78"
          y2="64"
          className="stroke-red-400/60 dark:stroke-red-400/50"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Right eye */}
        <line
          x1="116"
          y1="58"
          x2="122"
          y2="64"
          className="stroke-red-400/60 dark:stroke-red-400/50"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <line
          x1="122"
          y1="58"
          x2="116"
          y2="64"
          className="stroke-red-400/60 dark:stroke-red-400/50"
          strokeWidth="2"
          strokeLinecap="round"
        />
        {/* Sad mouth */}
        <path
          d="M88 82 Q100 74 112 82"
          className="stroke-red-400/60 dark:stroke-red-400/50"
          strokeWidth="2"
          strokeLinecap="round"
          fill="none"
        />
      </g>

      {/* Monitor stand */}
      <rect
        x="85"
        y="130"
        width="30"
        height="8"
        rx="2"
        className="fill-gray-200 dark:fill-gray-700"
      />
      <path
        d="M92 130 L95 140 H105 L108 130"
        className="fill-gray-200 dark:fill-gray-700"
      />

      {/* Monitor base */}
      <ellipse
        cx="100"
        cy="143"
        rx="28"
        ry="5"
        className="fill-gray-200 dark:fill-gray-700"
      />

      {/* Floating error particles */}
      <circle cx="55" cy="50" r="2" className="fill-red-400/70">
        <animate
          attributeName="cy"
          values="50;42;50"
          dur="2.5s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.7;0.3;0.7"
          dur="2.5s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="145" cy="45" r="1.5" className="fill-orange-400/60">
        <animate
          attributeName="cy"
          values="45;38;45"
          dur="3s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.6;0.2;0.6"
          dur="3s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="65" cy="95" r="1.8" className="fill-red-300/50">
        <animate
          attributeName="cy"
          values="95;88;95"
          dur="2.8s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.5;0.2;0.5"
          dur="2.8s"
          repeatCount="indefinite"
        />
      </circle>
      <circle cx="140" cy="100" r="2.2" className="fill-orange-300/50">
        <animate
          attributeName="cy"
          values="100;92;100"
          dur="3.2s"
          repeatCount="indefinite"
        />
        <animate
          attributeName="opacity"
          values="0.5;0.15;0.5"
          dur="3.2s"
          repeatCount="indefinite"
        />
      </circle>

      {/* Sparks / glitch lines */}
      <g>
        <line
          x1="38"
          y1="70"
          x2="30"
          y2="65"
          className="stroke-yellow-400/70"
          strokeWidth="1.5"
          strokeLinecap="round"
        >
          <animate
            attributeName="opacity"
            values="0;0.7;0"
            dur="1.8s"
            repeatCount="indefinite"
          />
        </line>
        <line
          x1="162"
          y1="55"
          x2="170"
          y2="50"
          className="stroke-yellow-400/70"
          strokeWidth="1.5"
          strokeLinecap="round"
        >
          <animate
            attributeName="opacity"
            values="0;0.7;0"
            dur="2.1s"
            repeatCount="indefinite"
          />
        </line>
        <line
          x1="35"
          y1="90"
          x2="28"
          y2="92"
          className="stroke-yellow-300/60"
          strokeWidth="1"
          strokeLinecap="round"
        >
          <animate
            attributeName="opacity"
            values="0;0.6;0"
            dur="2.4s"
            repeatCount="indefinite"
          />
        </line>
      </g>

      {/* Exclamation mark floating above */}
      <g className="animate-bounce-subtle">
        <circle
          cx="100"
          cy="18"
          r="10"
          className="fill-red-500 dark:fill-red-600"
        />
        <text
          x="100"
          y="23"
          textAnchor="middle"
          className="fill-white text-xs font-black"
          fontSize="14"
        >
          !
        </text>
      </g>
    </svg>
  );
}

// ─── Error Code Display ────────────────────────────────────────────────
function ErrorCodeBadge() {
  return (
    <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-red-200/60 bg-red-50/80 px-4 py-1.5 dark:border-red-800/40 dark:bg-red-950/50">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500" />
      </span>
      <span className="font-mono text-xs font-semibold text-red-600 dark:text-red-400">
        APPLICATION ERROR
      </span>
    </div>
  );
}

// ─── Floating Background Shapes ────────────────────────────────────────
function BackgroundShapes() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {/* Top-left blob */}
      <div className="absolute -left-20 -top-20 h-64 w-64 rounded-full bg-red-200/20 blur-3xl dark:bg-red-900/10" />
      {/* Bottom-right blob */}
      <div className="absolute -bottom-20 -right-20 h-72 w-72 rounded-full bg-orange-200/20 blur-3xl dark:bg-orange-900/10" />
      {/* Center subtle glow */}
      <div className="absolute left-1/2 top-1/3 h-40 w-40 -translate-x-1/2 rounded-full bg-red-100/30 blur-2xl dark:bg-red-800/10" />

      {/* Grid pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] dark:opacity-[0.05]"
        style={{
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
      />
    </div>
  );
}

/**
 * Global error boundary component with a stunning visual design.
 * Catches React errors and displays a beautiful, branded error screen.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback || (
          <div
            dir="rtl"
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-gray-50 via-white to-gray-100 p-4 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950"
          >
            <BackgroundShapes />

            <div className="relative z-10 flex w-full max-w-2xl flex-col items-center text-center">
              {/* Illustration */}
              <div className="mb-6">
                <BrokenScreenIllustration />
              </div>

              {/* Error badge */}
              <ErrorCodeBadge />

              {/* Title */}
              <h1 className="mb-2 text-3xl font-black tracking-tight text-gray-900 sm:text-4xl dark:text-gray-50">
                حدث خطأ غير متوقع
              </h1>

              {/* Description */}
              <p className="mb-8 max-w-md text-base leading-relaxed text-gray-500 dark:text-gray-400">
                نعتذر عن هذا الخطأ. حاول إعادة تحميل الصفحة أو العودة للصفحة
                الرئيسية
              </p>

              {/* Action buttons */}
              <div className="mb-8 flex flex-col gap-3 sm:flex-row">
                {/* Primary: Reload */}
                <button
                  onClick={() => window.location.reload()}
                  className="group inline-flex items-center justify-center gap-2.5 rounded-2xl bg-gray-900 px-7 py-3.5 text-sm font-bold text-white shadow-xl shadow-gray-900/20 transition-all duration-200 hover:-translate-y-0.5 hover:bg-gray-800 hover:shadow-2xl hover:shadow-gray-900/30 active:translate-y-0 dark:bg-white dark:text-gray-900 dark:shadow-white/10 dark:hover:bg-gray-100 dark:hover:shadow-white/20"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="transition-transform duration-300 group-hover:rotate-180"
                  >
                    <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                    <path d="M21 3v5h-5" />
                    <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                    <path d="M3 21v-5h5" />
                  </svg>
                  إعادة تحميل
                </button>

                {/* Secondary: Go Back */}
                <button
                  onClick={() => window.history.back()}
                  className="inline-flex items-center justify-center gap-2.5 rounded-2xl border-2 border-gray-200 bg-white/80 px-7 py-3.5 text-sm font-bold text-gray-700 shadow-lg shadow-gray-200/30 backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-white hover:shadow-xl active:translate-y-0 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-200 dark:shadow-gray-900/30 dark:hover:border-gray-600 dark:hover:bg-gray-800"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m9 18 6-6-6-6" />
                  </svg>
                  الرجوع
                </button>

                {/* Tertiary: Home */}
                <a
                  href="/"
                  className="inline-flex items-center justify-center gap-2.5 rounded-2xl border-2 border-gray-200 bg-white/80 px-7 py-3.5 text-sm font-bold text-gray-700 shadow-lg shadow-gray-200/30 backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-white hover:shadow-xl active:translate-y-0 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-200 dark:shadow-gray-900/30 dark:hover:border-gray-600 dark:hover:bg-gray-800"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                    <polyline points="9 22 9 12 15 12 15 22" />
                  </svg>
                  الرئيسية
                </a>
              </div>

              {/* Technical Details (Collapsible) */}
              {(this.state.error || this.state.errorInfo) && (
                <details className="group w-full max-w-lg">
                  <summary className="flex cursor-pointer items-center justify-center gap-2 rounded-xl px-4 py-2 text-xs font-medium text-gray-400 transition-colors hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="transition-transform group-open:rotate-180"
                    >
                      <path d="m6 9 6 6 6-6" />
                    </svg>
                    التفاصيل التقنية
                  </summary>

                  <div className="mt-3 overflow-hidden rounded-2xl border border-gray-200/80 bg-gray-50/80 backdrop-blur-sm dark:border-gray-700/50 dark:bg-gray-800/50">
                    <div className="flex items-center justify-between border-b border-gray-200/60 px-4 py-2.5 dark:border-gray-700/40">
                      <span className="text-2xs font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                        Error Details
                      </span>
                      <button
                        onClick={() => {
                          const text = [
                            this.state.error?.toString(),
                            this.state.errorInfo?.componentStack,
                          ]
                            .filter(Boolean)
                            .join('\n\n');
                          navigator.clipboard.writeText(text);
                        }}
                        className="rounded-lg px-2.5 py-1 text-2xs font-medium text-gray-400 transition-colors hover:bg-gray-200/60 hover:text-gray-600 dark:text-gray-500 dark:hover:bg-gray-700/60 dark:hover:text-gray-300"
                      >
                        نسخ
                      </button>
                    </div>
                    <pre
                      dir="ltr"
                      className="max-h-[200px] overflow-auto p-4 text-right font-mono text-2xs leading-relaxed text-gray-500 dark:text-gray-400"
                    >
                      {this.state.error?.toString()}
                      {this.state.errorInfo?.componentStack && (
                        <>
                          {'\n\nComponent Stack:\n'}
                          {this.state.errorInfo.componentStack}
                        </>
                      )}
                    </pre>
                  </div>
                </details>
              )}
            </div>
          </div>
        )
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
